import { isInMobile } from '@/src/hooks/mobile';
import { useClientLayoutEffect } from '@/src/hooks/useClientLayoutEffect';
import type { MobileOS } from '@/src/modules/mobile/mobile.types';
import { noop } from '@/src/utils/noop';
import { detectMobile } from '@fabric-app/utils/detection';
import { omit } from '@fabric-app/utils/object/manipulation';
import { useRouter } from 'next/router';
import type { PropsWithChildren } from 'react';
import React, { createContext, useEffect, useRef, useState } from 'react';

interface OpenInNativeDrawerContextValue {
  mobileOS: MobileOS | undefined;
  isVisible: boolean;
  isPending: boolean;

  urlPath: string;

  closeDrawer: () => void;
}

const OpenInNativeDrawerContext = createContext({
  mobileOS: undefined,
  isVisible: false,
  isPending: false,

  urlPath: '',

  closeDrawer: noop,
} as OpenInNativeDrawerContextValue);

export const useOpenInNativeDrawerContext = () => {
  return React.useContext(OpenInNativeDrawerContext);
};

export const OpenInNativeDrawerProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [pending, setPending] = useState(!isInMobile());
  const [mobileOS, setMobileOS] = useState<MobileOS | false>(false);
  const [urlPath, setUrlPath] = useState<string>('');

  const router = useRouter();

  useClientLayoutEffect(() => {
    const checkShowDrawer = () => {
      // Already in the mobile app, return
      if (isInMobile()) return;

      // check for ?deeplink=true query param and return if it's present
      if (window.location.search.includes('deeplink=true')) return;

      setMobileOS(detectMobile());

      let path = window.location.pathname;
      // we add search with a deeplink query param, if the user does not have the app installed
      // it will check for the deeplink to avoid showing the drawer again
      if (window.location.search) path += window.location.search + '&deeplink=true';
      else path += '?deeplink=true';

      setUrlPath(path);
    };

    checkShowDrawer();
    setPending(window.location.search.includes('deeplinkFallback=') && !isInMobile());
  }, []);

  const ranFallback = useRef(false);
  useClientLayoutEffect(() => {
    if (!router.query.deeplinkFallback || ranFallback.current) return;
    ranFallback.current = true;

    const query = omit(router.query, ['deeplinkFallback']);

    if (!isInMobile() && detectMobile() === 'iOS')
      window.location.href = router.query.deeplinkFallback as string;

    setTimeout(() => {
      router.replace(
        {
          pathname: router.pathname,
          query,
        },
        undefined,
        { shallow: true },
      );
      setPending(false);
    }, 0);
  }, [router]);

  // cleanup deeplink query param
  useEffect(() => {
    if (!router.query.deeplink) return;

    const query = omit(router.query, ['deeplink']);

    router.replace(
      {
        pathname: router.pathname,
        query,
      },
      undefined,
      { shallow: true },
    );
  }, [router]);

  const closeDrawer = () => {
    setMobileOS(false);
  };

  return (
    <OpenInNativeDrawerContext.Provider
      value={{
        mobileOS: mobileOS || undefined,
        isVisible: !!mobileOS,
        isPending: pending,
        urlPath,
        closeDrawer,
      }}
    >
      {children}
    </OpenInNativeDrawerContext.Provider>
  );
};
